/**
 * ehr#682 (PROGRESS_NOTE_10-29-V131 "one open copy per note"): only one copy of a progress note
 * can be edited at a time in this browser. Without it, two tabs (or windows) of the same note
 * save over each other, last write wins.
 *
 *   - The copy that opens first holds the note. It writes a heartbeat to localStorage
 *     (`credify.pn.open.<noteId>` = { tab, at }) every 2 s and announces changes on a
 *     BroadcastChannel (the `storage` event is the fallback where BroadcastChannel is missing).
 *   - A second copy opens read-only ("This note is open in another tab").
 *   - Closing the holder (pagehide, or leaving the note) marks the record closed instead of
 *     removing it. A read-only copy takes the note over on its own once it has been closed for
 *     3 s, so a reload of the holder gets it back first.
 *   - A holder that only goes quiet (print dialog, background tab) keeps the note: a read-only
 *     copy never takes a record that is old but not closed. Open here instead always works.
 *   - Open here instead: this copy claims the note, the holder saves what it has unsaved, turns
 *     read-only and answers; this copy then reloads the saved note and can be edited.
 *
 * The tab id is new on every page load (not read from sessionStorage, which Chrome's Duplicate
 * tab copies: V131's open item). sessionStorage only remembers the previous page's id in this tab,
 * so a reload can tell "my own closed record" from "a live copy in another tab".
 *
 * STAND-IN, as in V131: this covers one browser. Other devices and other people need a server
 * lock per note (a follow-up for Jay).
 *
 * What is locked (lockKeyFor): the key names one thing that two copies could save over.
 *   - `<noteId>`: the author's editable copy of an individual note (ehr#682, unchanged).
 *   - `family:<sessionId>`: a family / couples session. One note per attendee, but the page
 *     also saves the shared Session summary, Family or Couples, the attendees, Attendance for
 *     everyone and Sign ready notes, which write other attendees' notes. Two copies of the page
 *     on different attendees would still save over each other, so the whole session is one key,
 *     on the session page and wherever one of its notes opens on its own.
 *   - `review:<noteId>`: the reviewer's (co-signer's) copy of a note waiting for review. Kept
 *     apart from the author's key: the author never reviews their own note, and the two turns
 *     never overlap (the author edits a draft or a returned note, the reviewer a pending one).
 *   - `group:<sessionId>`: a group session. The group page holds it, and a group member's note
 *     opened on its own (from To review, or a ?note= link) holds the same key, as family does, so
 *     the page and the lone note never save over each other (#695 follow-up).
 */

export const LOCK_PREFIX = 'credify.pn.open.';
export const PREV_TAB_KEY = 'credify.pn.tab';
export const HELD_KEY = 'credify.pn.held';
export const CHANNEL_NAME = 'credify-note-lock';
export const BEAT_MS = 2000;
export const CHECK_MS = 1000;
/** A record older than this, and not closed, may be taken by a copy that is just opening. */
export const STALE_MS = 6000;
/** A closed record waits this long before a read-only copy takes it (a reload gets it first). */
export const GRACE_MS = 3000;
/** Open here instead waits at most this long for the holder to save and answer. */
export const HANDOFF_MS = 2500;
/** After a reload, the note this tab held is kept for it this long while the page reopens it. */
export const RESERVE_MS = 10000;

export interface LockRecord {
  tab: string;
  at: number;
  closed?: true;
}
export type LockMessage = {
  note: string;
  tab: string;
  kind: 'claim' | 'released' | 'closed' | 'beat' | 'saved';
  /** On 'released': the holder saved its unsaved changes before turning read-only. */
  saved?: boolean;
};

interface KV {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
  removeItem?(k: string): void;
}
interface Channel {
  postMessage(m: LockMessage): void;
  close(): void;
  onmessage: ((e: { data: LockMessage }) => void) | null;
}

export interface LockEnv {
  store: KV | null;
  session: KV | null;
  channel: () => Channel | null;
  now: () => number;
  setInterval: (fn: () => void, ms: number) => unknown;
  clearInterval: (h: unknown) => void;
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (h: unknown) => void;
  /** Listen to window events ('storage', 'pagehide', 'pageshow', 'visibilitychange'). */
  on: (type: string, fn: (e: Event) => void) => () => void;
}

export type LockState = 'editable' | 'readonly' | 'opening';
export type LockReason = 'opened-elsewhere' | 'other-closed' | 'opened-here';

export interface LockHooks {
  /** The view changed (editable, read-only, or taking over). */
  onState: (s: LockState) => void;
  /** This copy lost the note to another one: save what is unsaved, then resolve. */
  onLost: () => Promise<boolean>;
  /** This copy can edit again: reload the saved note first. */
  onRegained: (reason: Exclude<LockReason, 'opened-elsewhere'>) => void;
  /** The holder saved (or the old holder saved as this copy took over): reload quietly. */
  onRemoteSaved: () => void;
}

const newId = () =>
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36)
  ).replace(/-/g, '');

function readJson<T>(kv: KV | null, k: string): T | null {
  try {
    const v = kv?.getItem(k);
    return v ? (JSON.parse(v) as T) : null;
  } catch {
    return null;
  }
}
function writeJson(kv: KV | null, k: string, v: unknown): void {
  try {
    kv?.setItem(k, JSON.stringify(v));
  } catch {
    // storage full or blocked: the lock is best effort
  }
}

/** A page load's identity: a fresh tab id, and the id of the page before it in this tab. */
export interface TabIdentity {
  id: string;
  prev: string | null;
}
export function tabIdentity(session: KV | null, makeId: () => string = newId): TabIdentity {
  const id = makeId();
  let prev: string | null = null;
  try {
    prev = session?.getItem(PREV_TAB_KEY) ?? null;
    session?.setItem(PREV_TAB_KEY, id);
  } catch {
    prev = null;
  }
  return { id, prev };
}

/** A family / couples session's key (the session page and any of its notes opened alone). */
export const familyLockKey = (sessionId: string) => `family:${sessionId}`;
/** A group session: the group page, or one of its members' notes opened on its own. */
export const groupLockKey = (sessionId: string) => `group:${sessionId}`;
/** The reviewer's copy of a note waiting for review. */
export const reviewLockKey = (noteId: string) => `review:${noteId}`;

/**
 * The key one copy of a note in the progress note page holds, or null for no lock. `editable`:
 * the author's own draft or returned note. `reviewing`: the reviewer's turn on a pending note.
 * `familySessionId`: the note is an attendee's in that family / couples session; `groupSessionId`:
 * a member's in that group session. `inSessionPage`: the note is shown inside a family or group
 * session page, which holds the session's key itself.
 */
export function lockKeyFor(o: {
  noteId: string | null;
  loaded: boolean;
  editable: boolean;
  reviewing: boolean;
  familySessionId?: string | null;
  groupSessionId?: string | null;
  inSessionPage?: boolean;
}): string | null {
  if (!o.loaded || !o.noteId || o.inSessionPage) return null;
  if (!o.editable && !o.reviewing) return null;
  if (o.familySessionId) return familyLockKey(o.familySessionId);
  if (o.groupSessionId) return groupLockKey(o.groupSessionId);
  return o.editable ? o.noteId : reviewLockKey(o.noteId);
}

const lockKey = (note: string) => LOCK_PREFIX + note;
export const readLock = (store: KV | null, note: string) =>
  readJson<LockRecord>(store, lockKey(note));

/** Held by a live copy other than this one. */
export function heldElsewhere(rec: LockRecord | null, tab: string, now: number): boolean {
  return !!rec && rec.tab !== tab && !rec.closed && now - rec.at < STALE_MS;
}

function heldList(session: KV | null): string[] {
  const v = readJson<unknown>(session, HELD_KEY);
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}
function setHeld(session: KV | null, note: string, on: boolean): void {
  const list = heldList(session).filter((n) => n !== note);
  writeJson(session, HELD_KEY, on ? [...list, note] : list);
}

/**
 * On page load: the notes the previous page in this tab held and closed (a reload) are kept for
 * this page for a few seconds, so a read-only copy elsewhere doesn't take them while the page
 * reopens the note. Returns a function that cancels the keep-alive for one note.
 */
export function reserveAfterReload(
  env: LockEnv,
  me: TabIdentity,
): { claim: (note: string) => void } {
  const timers = new Map<string, unknown>();
  if (me.prev) {
    for (const note of heldList(env.session)) {
      const rec = readLock(env.store, note);
      if (!rec || rec.tab !== me.prev || !rec.closed) {
        setHeld(env.session, note, false);
        continue;
      }
      const started = env.now();
      writeJson(env.store, lockKey(note), { tab: me.id, at: started });
      const h = env.setInterval(() => {
        const r = readLock(env.store, note);
        if (!r || r.tab !== me.id) {
          env.clearInterval(h);
          timers.delete(note);
          return;
        }
        if (env.now() - started >= RESERVE_MS) {
          // the page didn't reopen it: let the other copy have it
          writeJson(env.store, lockKey(note), { tab: me.id, at: env.now(), closed: true });
          setHeld(env.session, note, false);
          env.clearInterval(h);
          timers.delete(note);
          return;
        }
        writeJson(env.store, lockKey(note), { tab: me.id, at: env.now() });
      }, BEAT_MS);
      timers.set(note, h);
    }
  }
  return {
    claim: (note) => {
      const h = timers.get(note);
      if (h !== undefined) env.clearInterval(h);
      timers.delete(note);
    },
  };
}

export class NoteTabLock {
  state: LockState = 'readonly';
  private ch: Channel | null = null;
  private timer: unknown = null;
  private handoff: unknown = null;
  private lastBeat = 0;
  private off: (() => void)[] = [];
  private stopped = false;
  /** After pagehide: the page is going (or into the back-forward cache); no more heartbeats. */
  private hidden = false;
  /** Set while this copy has just claimed the note and the old holder may still save. */
  private claimedAt = 0;

  constructor(
    private readonly note: string,
    private readonly me: TabIdentity,
    private readonly env: LockEnv,
    private readonly hooks: LockHooks,
  ) {}

  private read() {
    return readLock(this.env.store, this.note);
  }
  private write(closed = false) {
    const rec: LockRecord = { tab: this.me.id, at: this.env.now() };
    if (closed) rec.closed = true;
    writeJson(this.env.store, lockKey(this.note), rec);
    this.lastBeat = rec.at;
  }
  private post(kind: LockMessage['kind'], saved?: boolean) {
    try {
      this.ch?.postMessage({
        note: this.note,
        tab: this.me.id,
        kind,
        ...(saved === undefined ? {} : { saved }),
      });
    } catch {
      // channel closed
    }
  }
  private set(s: LockState) {
    if (this.state === s) return;
    this.state = s;
    this.hooks.onState(s);
  }

  start(): LockState {
    this.ch = this.env.channel();
    if (this.ch) this.ch.onmessage = (e) => this.onMessage(e.data);
    this.off.push(
      this.env.on('storage', (e) => {
        if ((e as StorageEvent).key === lockKey(this.note)) this.check();
      }),
      this.env.on('pagehide', () => {
        this.release();
        this.hidden = true;
      }),
      this.env.on('pageshow', () => {
        this.hidden = false;
        this.check(true);
      }),
      this.env.on('visibilitychange', () => this.check(true)),
    );
    const rec = this.read();
    const now = this.env.now();
    const reload = !!rec && !!rec.closed && rec.tab === this.me.prev;
    if (heldElsewhere(rec, this.me.id, now) && !reload) {
      this.state = 'readonly';
    } else {
      this.state = 'editable';
      this.write();
      this.claimedAt = now;
      setHeld(this.env.session, this.note, true);
      this.post('beat');
    }
    this.timer = this.env.setInterval(() => this.check(), CHECK_MS);
    return this.state;
  }

  /** Leaving the note (or the page): mark the record closed so a read-only copy can take it. */
  release(): void {
    const rec = this.read();
    if (rec && rec.tab === this.me.id && !rec.closed) {
      this.write(true);
      this.post('closed');
    }
  }

  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    this.release();
    setHeld(this.env.session, this.note, false);
    if (this.timer !== null) this.env.clearInterval(this.timer);
    if (this.handoff !== null) this.env.clearTimeout(this.handoff);
    for (const f of this.off) f();
    this.off = [];
    try {
      this.ch?.close();
    } catch {
      // already closed
    }
    this.ch = null;
  }

  /** The holder saved: read-only copies reload. */
  notifySaved(): void {
    if (this.state === 'editable') this.post('saved');
  }

  /** Open here instead. */
  takeOver(): void {
    if (this.state !== 'readonly' || this.stopped) return;
    const rec = this.read();
    const holder = rec && !rec.closed && rec.tab !== this.me.id ? rec.tab : null;
    this.write();
    setHeld(this.env.session, this.note, true);
    this.claimedAt = this.env.now();
    this.post('claim');
    if (!holder) return this.regain('opened-here');
    this.set('opening');
    this.handoff = this.env.setTimeout(() => this.regain('opened-here'), HANDOFF_MS);
  }

  private regain(reason: 'other-closed' | 'opened-here') {
    if (this.handoff !== null) this.env.clearTimeout(this.handoff);
    this.handoff = null;
    if (this.stopped) return;
    this.write();
    this.set('editable');
    this.hooks.onRegained(reason);
  }

  private lose() {
    if (this.state === 'readonly') return;
    const was = this.state;
    this.set('readonly');
    if (was !== 'editable') return;
    this.hooks
      .onLost()
      .catch(() => false)
      .then((saved) => this.post('released', saved));
  }

  private onMessage(m: LockMessage) {
    if (!m || m.note !== this.note || m.tab === this.me.id || this.stopped) return;
    if (m.kind === 'claim') {
      if (this.state !== 'readonly') this.lose();
      return;
    }
    if (m.kind === 'released') {
      if (this.state === 'opening') this.regain('opened-here');
      // a copy that just claimed the note at open: the old holder saved after we loaded
      else if (
        this.state === 'editable' &&
        m.saved &&
        this.env.now() - this.claimedAt < HANDOFF_MS * 2
      )
        this.hooks.onRemoteSaved();
      return;
    }
    if (m.kind === 'saved') {
      if (this.state === 'readonly') this.hooks.onRemoteSaved();
      return;
    }
    this.check();
  }

  /** Runs every second, on storage and channel events, and when the tab is shown again. */
  check(force = false): void {
    // a visibilitychange or a heartbeat after pagehide must not take a closed record back
    if (this.stopped || this.hidden) return;
    const rec = this.read();
    const now = this.env.now();
    if (this.state === 'editable') {
      if (heldElsewhere(rec, this.me.id, now)) return this.lose();
      if (force || !rec || rec.tab !== this.me.id || rec.closed || now - this.lastBeat >= BEAT_MS)
        this.write();
      return;
    }
    if (this.state === 'readonly') {
      if (!rec || rec.tab === this.me.id) return this.regain('other-closed');
      if (rec.closed && (rec.tab === this.me.prev || now - rec.at > GRACE_MS)) {
        this.write();
        this.claimedAt = now;
        this.post('claim');
        return this.regain('other-closed');
      }
    }
  }
}

/** The browser's own environment. */
export function browserLockEnv(): LockEnv {
  const safe = <T>(f: () => T): T | null => {
    try {
      return f();
    } catch {
      return null;
    }
  };
  return {
    store: safe(() => window.localStorage),
    session: safe(() => window.sessionStorage),
    channel: () =>
      typeof BroadcastChannel === 'function'
        ? (safe(() => new BroadcastChannel(CHANNEL_NAME)) as unknown as Channel | null)
        : null,
    now: () => Date.now(),
    setInterval: (fn, ms) => window.setInterval(fn, ms),
    clearInterval: (h) => window.clearInterval(h as number),
    setTimeout: (fn, ms) => window.setTimeout(fn, ms),
    clearTimeout: (h) => window.clearTimeout(h as number),
    on: (type, fn) => {
      const target: EventTarget = type === 'visibilitychange' ? document : window;
      target.addEventListener(type, fn);
      return () => target.removeEventListener(type, fn);
    },
  };
}

let page: { env: LockEnv; me: TabIdentity; reserve: { claim: (n: string) => void } } | null = null;
/** This page load's lock environment, set up once (a reload's held notes are kept for it). */
export function pageLock() {
  if (!page) {
    const env = browserLockEnv();
    const me = tabIdentity(env.session);
    page = { env, me, reserve: reserveAfterReload(env, me) };
  }
  return page;
}
